import React, { useRef, useState } from 'react';
import CategorySelector from './CategorySelect';
import { showToast } from '../../common/components/Toast';
import { uploadImage } from '../../../api/products';
import { errorMessage } from '../../../api';

const AddProductModal = ({ isOpen, onClose, onAdd, categories, setProducts, fetchProducts }) => {
  // 대부분의 상품은 수량을 세지 않는다(부품을 필요할 때 들여온다). 기본값을 무한으로 두어
  // 직원이 무한을 뜻하려고 9를 길게 입력하던 관행을 없앤다.
  const [newProduct, setNewProduct] = useState({
    name: '',
    price: '',
    stock: '',
    category: '',
    imageFile: null,
    unlimitedStock: true,
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewProduct((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0] || null;
    // 서버 한도(8MB)를 넘기면 업로드가 거부되므로 미리 걸러 안내한다
    if (file && file.size > 8 * 1024 * 1024) {
      showToast("이미지 용량이 너무 큽니다. 8MB 이하로 올려주세요.", "fail");
      e.target.value = "";
      setNewProduct((prev) => ({ ...prev, imageFile: null }));
      setPreviewUrl(null);
      return;
    }
    setNewProduct((prev) => ({ ...prev, imageFile: file }));
    setPreviewUrl(file ? URL.createObjectURL(file) : null);
  };

  const handleCategorySelect = (category) => {
    setNewProduct((prev) => ({ ...prev, category }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("🛠 [handleSubmit 실행됨] 상품 추가 요청 시작");
  console.log("📌 onAdd 존재 여부:", onAdd);

  if (!onAdd) {
    console.error("❌ onAdd 함수가 존재하지 않습니다.");
    return;
  }

    if (!newProduct.name) {
      showToast("상품명을 입력해 주세요!", "fail");
      return;
    }
    
    if (!newProduct.price) {
      showToast("가격을 입력해 주세요!", "fail");
      return;
    }
    
    if (!newProduct.category) {
      showToast("카테고리를 선택해 주세요!", "fail");
      return;
    }
    
    const storeId = localStorage.getItem("selected_store_id");
    if (!storeId) {
        console.error("❌ store_id가 없습니다.");
        showToast("스토어 정보를 찾을 수 없습니다.", "fail");
        return;
    }

    if (!newProduct.unlimitedStock && newProduct.stock === '') {
      showToast("재고 수량을 입력하거나 '수량 제한 없음'을 선택해 주세요.", "fail");
      return;
    }

    setIsSaving(true);
    try {
      // 이미지를 고르지 않으면 서버가 기본 이미지 주소를 채운다
      let imageUrl = null;

      if (newProduct.imageFile) {
          console.log("📌 이미지 업로드 시작...");
          const uploadedImageUrl = await uploadImage(newProduct.imageFile);
          if (uploadedImageUrl) {
              imageUrl = `${uploadedImageUrl}?timestamp=${Date.now()}`;
          }
          console.log("✅ 이미지 업로드 완료:", imageUrl);
      }


      const formattedProduct = {
          name: newProduct.name,
          price: parseInt(newProduct.price, 10),
          // 무한이면 서버가 재고 값을 정한다(구 버전 화면 호환값). 여기서 보내는 0 은 무시된다.
          stock: newProduct.unlimitedStock ? 0 : parseInt(newProduct.stock, 10),
          category: newProduct.category,
          image_url: imageUrl,
          store_id: parseInt(storeId, 10),
          unlimited_stock: newProduct.unlimitedStock
      };

      console.log("📌 [addProduct 호출] 요청 데이터:", formattedProduct);
      const addedProduct = await onAdd(formattedProduct);

      console.log("📌 [addProduct 응답 확인]:", addedProduct);

      if (addedProduct) {
          console.log("✅ 상품 추가 완료:", addedProduct);
          setProducts((prevProducts) => [addedProduct, ...prevProducts]);
          showToast("상품이 추가되었습니다!", "success");
          onClose();

          fetchProducts();
      } else {
          console.error("❌ addProduct 호출 후 응답이 없음");
          showToast("상품 추가 중 오류가 발생했습니다.", "fail");
      }
  } catch (error) {
      console.error("❌ 상품 추가 중 오류:", error);
      // 등록되지 않은 카테고리 등 서버가 사유를 준 경우 그대로 보여준다
      showToast(errorMessage(error, "상품 추가 중 오류가 발생했습니다."), "fail");
  } finally {
      setIsSaving(false);
  }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2 className="text-lg font-bold mb-4">상품 추가</h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-500 text-gray-900">상품명</label>
            <input
              type="text" name="name" placeholder="예: PCX 배터리"
              value={newProduct.name} onChange={handleChange}
              className="w-full px-4 py-2.5 text-base text-gray-900 border border-gray-900 rounded-2xl placeholder:text-gray-400"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-500 text-gray-900">가격</label>
            <input
              type="number" name="price" placeholder="0"
              value={newProduct.price} onChange={handleChange}
              className="w-full px-4 py-2.5 text-base text-gray-900 border border-gray-900 rounded-2xl placeholder:text-gray-400"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-500 text-gray-900">재고</label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox" name="unlimitedStock"
                checked={newProduct.unlimitedStock} onChange={handleChange}
                className="w-5 h-5"
              />
              <span className="text-sm text-gray-700">수량 제한 없음</span>
            </label>
            {/* 수량을 세기로 한 경우에만 입력칸을 연다 — 무한일 때 숫자가 보이면
                그 숫자가 유효한 재고라고 오해하게 된다 */}
            {!newProduct.unlimitedStock && (
              <input
                type="number" name="stock" placeholder="수량을 입력하세요" min="0"
                value={newProduct.stock} onChange={handleChange}
                className="w-full px-4 py-2.5 text-base text-gray-900 border border-gray-900 rounded-2xl placeholder:text-gray-400"
              />
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-500 text-gray-900">카테고리</label>
            <CategorySelector
              categories={categories}
              selectedCategory={newProduct.category}
              onSelect={handleCategorySelect}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-sm font-500 text-gray-900">상품 이미지</label>
            {/* label로 파일창을 연다 — 숨긴 입력을 JS로 클릭하면 브라우저가 막을 수 있다 */}
            <input
              ref={fileInputRef} id="product-image-input" type="file" name="imageFile"
              accept="image/*" onChange={handleFileChange}
              style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            />
            <div className="flex items-center gap-2">
              <label
                htmlFor="product-image-input"
                className="px-5 py-2 text-sm border border-gray-900 text-gray-900 rounded-2xl cursor-pointer hover:bg-gray-900 hover:text-white whitespace-nowrap"
              >
                이미지 선택
              </label>
              <span className="text-sm text-gray-500">
                {newProduct.imageFile ? newProduct.imageFile.name : "선택 안 함 (기본 이미지로 등록)"}
              </span>
            </div>
            {previewUrl && (
              <img src={previewUrl} alt="미리보기" className="w-16 h-16 object-contain mt-1" />
            )}
          </div>

          <div className="flex gap-2 mt-2">
            <button
              type="submit" disabled={isSaving}
              className="px-5 py-2 text-sm bg-gray-900 text-white rounded-2xl disabled:bg-gray-400"
            >
              {isSaving ? "추가 중..." : "추가"}
            </button>
            <button
              type="button" onClick={onClose} disabled={isSaving}
              className="px-5 py-2 text-sm border border-gray-900 text-gray-900 rounded-2xl hover:bg-gray-900 hover:text-white"
            >
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductModal;
